import Button from "components/form/Button";
import moment from "moment";
import { useAppDispatch } from "store/hooks";
import { setIsPaymentProcessing } from "store/slices/paymentsSlice";
import { formatCurrency, rowHelperText } from "utils/HelperFunctions";
import { useTid, useTestAttrsFactory } from "utils/testid";
import { formatDisplayDate } from "utils/DateFormat";
interface AgreementDetailsProps {
  currentStep: number;
  tempdata: any;
  handleNext: (payload: any) => void;
  isFee?: boolean;
}
const AgreementDetails = ({
  currentStep,
  tempdata,
  handleNext,
  isFee,
}: AgreementDetailsProps) => {
  const dispatch = useAppDispatch();
  const tid = useTid();
  const testAttrs = useTestAttrsFactory();
  // Wider screens show the step as a capped panel. There the installment rows
  // scroll on their own while the column header, Total and Next stay put, so a
  // long schedule can't push Total and Next out of view.
  const isCappedPanel = window.innerWidth > 769;

  return (
    <>
      <div
        className="text-xl lg:h-fit h-full font-medium w-full"
        {...testAttrs("agreement-details-step", "Agreement details step")}
      >
        <p
          className={`${
            currentStep === 1 ? "text-btnPrimaryActive" : "text-txtPrimary"
          } relative lg:flex lg:h-10 lg:items-center`}
        >
          Agreement details
        </p>
        <div
          className={`${isCappedPanel && "answerSection agreementSchedule"} ${
            currentStep === 1 ? "open" : ""
          } w-full`}
        >
          <div
            className={`text-LightTextParagraph mt-2 w-full ${
              isCappedPanel ? "flex min-h-0 flex-1 flex-col" : ""
            }`}
          >
            <div
              className={`overflow-x-scroll w-full ${
                isCappedPanel ? "flex min-h-0 flex-1 flex-col" : ""
              }`}
              {...testAttrs("installments", "Agreement installments")}
            >
              <div
                className={`flex items-center w-full justify-between mt-6 min-w-[320px] ${
                  isCappedPanel ? "shrink-0 pr-6" : ""
                }`}
              >
                <div
                  className={`lg:w-[10%] w-[7%] text-sm text-txtParagraph leading-normal text-start min-w-[26px]`}
                >
                  #
                </div>
                <div
                  className={`lg:w-[28%] w-[25%] text-sm text-txtParagraph leading-normal text-left min-w-[96px]`}
                >
                  Description
                </div>
                <div
                  className={`${
                    isFee ? "lg:w-[22%] w-[25%]" : "lg:w-[20%] w-[25%]"
                  } text-sm text-txtParagraph leading-normal text-left min-w-[96px]`}
                >
                  Payment Date
                </div>
                {isFee && (
                  <>
                    <div
                      className={`${"w-[10%]"} text-sm text-txtParagraph leading-normal text-start min-w-[56px]`}
                    >
                      Fee
                    </div>
                  </>
                )}

                <div
                  className={`w-[20%] text-sm text-txtParagraph leading-normal text-end min-w-[96px]`}
                >
                  Amount
                </div>
              </div>
              <div
                className={`flex flex-col gap-[13px] mt-[10px] ${
                  isCappedPanel
                    ? "min-h-0 flex-1 overflow-y-auto pr-4 [scrollbar-gutter:stable]"
                    : ""
                }`}
                {...testAttrs("installment-rows", "Installment schedule")}
              >
                {tempdata.map((details: any, index: number) => {
                  const installmentKey =
                    details?.id ||
                    details?.schedule_id ||
                    details?.billing_date ||
                    index + 1;
                  return (
                    <div
                      key={installmentKey}
                      data-testid={tid(`installment-row-${installmentKey}`)}
                    >
                      <div
                        className={`h-[1px] w-full bg-strokePrimary mb-[14px] ${
                          isFee ? "min-w-[374px]" : "min-w-[314px]"
                        }`}
                      />
                      <div className="flex justify-between items-center">
                        <p className="lg:w-[10%] w-[7%] text-sm text-txtParagraph font-normal min-w-[26px]">
                          {index + 1}
                          <span className="text-xs lowercase">
                            {rowHelperText(index + 1)}
                          </span>
                        </p>
                        <p className="lg:w-[28%] w-[25%] text-sm text-txtPrimary text-left font-normal min-w-[96px] ">
                          Installment {index + 1}
                        </p>
                        <p
                          className={`${
                            isFee ? "lg:w-[22%] w-[25%]" : "lg:w-[20%] w-[25%]"
                          } text-sm text-txtPrimary font-normal text-left min-w-[96px]`}
                        >
                          {formatDisplayDate(
                            details?.billing_date,
                            "MM/DD/YYYY"
                          )}
                        </p>
                        {isFee && (
                          <>
                            <p
                              className={`${"w-[10%]"} text-sm text-txtPrimary leading-normal text-start min-w-[56px]`}
                            >
                              {formatCurrency(details.cf_amount) || "$0.00"}
                            </p>
                          </>
                        )}
                        <p className="w-[20%] text-lg text-txtPrimary font-medium text-end min-w-[96px]">
                          {formatCurrency(details.amount) || "$0.00"}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div
                className={`h-[1px] w-full shrink-0 bg-strokePrimary mt-4 ${
                  isFee ? "min-w-[374px]" : "min-w-[314px]"
                }`}
              />
            </div>
            <div className="font-bold text-txtPrimary flex shrink-0 justify-between items-center py-4">
              <p className="text-lg uppercase">Total</p>
              <p className="text-2xl font-medium">
                {formatCurrency(
                  tempdata.reduce(
                    (sum: any, item: any) => sum + Number(item?.amount),
                    0
                  )
                ) || "$0.00"}
              </p>
            </div>
            <div className="flex shrink-0 justify-end">
              <Button
                text="Next"
                className="!w-40"
                variant="variable"
                {...testAttrs(
                  "agreement-details-next",
                  "Continue to signature"
                )}
                onClick={() => {
                  dispatch(setIsPaymentProcessing(true));
                  handleNext({});
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AgreementDetails;
